import React, { useState, forwardRef, useImperativeHandle } from "react";
import { CheckOutlined } from "@ant-design/icons";

// Define types for the component
interface CompletedSteps {
    "collecting-context": {
        "variables-data-structure": boolean;
        "read-user-inputs": boolean;
        "derived-variables": boolean;
    };
    "generating-functions": {
        processing: boolean;
    };
}

interface ProgressDataItem {
    id: keyof CompletedSteps;
    title: string;
    substeps: Array<{
        id: string;
        title: string;
    }>;
}

interface AutomationProgressComponentProps {
    // Optional props to control the component from parent
    completedSteps?: CompletedSteps;
    onCompletedStepsChange?: (steps: CompletedSteps) => void;
    readOnly?: boolean;
    className?: string;
}

export interface AutomationProgressComponentRef {
    completeAllSteps: () => void;
    resetAllSteps: () => void;
    setStepCompletion: (
        stepId: keyof CompletedSteps,
        substepId: string,
        completed: boolean,
    ) => void;
    getOverallProgress: () => {
        completed: number;
        total: number;
        percentage: number;
    };
    getCompletedSteps: () => CompletedSteps;
}

const AutomationProgressComponent = forwardRef<
    AutomationProgressComponentRef,
    AutomationProgressComponentProps
>(
    (
        {
            completedSteps: externalCompletedSteps,
            onCompletedStepsChange,
            readOnly = false,
            className = "",
        },
        ref,
    ) => {
        const [internalCompletedSteps, setInternalCompletedSteps] =
            useState<CompletedSteps>({
                "collecting-context": {
                    "variables-data-structure": false,
                    "read-user-inputs": false,
                    "derived-variables": false,
                },
                "generating-functions": {
                    processing: false,
                },
            });

        // Use external state if provided, otherwise use internal state
        const completedSteps = externalCompletedSteps || internalCompletedSteps;
        const setCompletedSteps =
            onCompletedStepsChange || setInternalCompletedSteps;

        const progressData: ProgressDataItem[] = [
            {
                id: "collecting-context",
                title: "Collecting Context",
                substeps: [
                    {
                        id: "variables-data-structure",
                        title: "Variables Data Structure",
                    },
                    { id: "read-user-inputs", title: "Read User Inputs" },
                    { id: "derived-variables", title: "Derived Variables" },
                ],
            },
            {
                id: "generating-functions",
                title: "Generating Functions",
                substeps: [{ id: "processing", title: "Processing" }],
            },
        ];

        const toggleStepCompletion = (
            stepId: keyof CompletedSteps,
            substepId: string,
        ) => {
            if (readOnly) return;

            if (onCompletedStepsChange) {
                // External control - call the callback
                onCompletedStepsChange({
                    ...completedSteps,
                    [stepId]: {
                        ...completedSteps[stepId],
                        [substepId]:
                            !completedSteps[stepId][
                                substepId as keyof (typeof completedSteps)[typeof stepId]
                            ],
                    },
                });
            } else {
                // Internal control - use state setter
                setInternalCompletedSteps((prev: CompletedSteps) => ({
                    ...prev,
                    [stepId]: {
                        ...prev[stepId],
                        [substepId]:
                            !prev[stepId][
                                substepId as keyof (typeof prev)[typeof stepId]
                            ],
                    },
                }));
            }
        };

        const isStepCompleted = (stepId: keyof CompletedSteps): boolean => {
            const stepData = completedSteps[stepId];
            return Object.values(stepData).every((completed) => completed);
        };

        const getStepProgress = (stepId: keyof CompletedSteps) => {
            const stepData = completedSteps[stepId];
            const completed = Object.values(stepData).filter(Boolean).length;
            const total = Object.values(stepData).length;
            return { completed, total };
        };

        // Helper methods that can be called from parent components
        const completeAllSteps = () => {
            if (readOnly) return;

            const allCompleted: CompletedSteps = {
                "collecting-context": {
                    "variables-data-structure": true,
                    "read-user-inputs": true,
                    "derived-variables": true,
                },
                "generating-functions": {
                    processing: true,
                },
            };

            if (onCompletedStepsChange) {
                onCompletedStepsChange(allCompleted);
            } else {
                setInternalCompletedSteps(allCompleted);
            }
        };

        const resetAllSteps = () => {
            if (readOnly) return;

            const allReset: CompletedSteps = {
                "collecting-context": {
                    "variables-data-structure": false,
                    "read-user-inputs": false,
                    "derived-variables": false,
                },
                "generating-functions": {
                    processing: false,
                },
            };

            if (onCompletedStepsChange) {
                onCompletedStepsChange(allReset);
            } else {
                setInternalCompletedSteps(allReset);
            }
        };

        const setStepCompletion = (
            stepId: keyof CompletedSteps,
            substepId: string,
            completed: boolean,
        ) => {
            if (readOnly) return;

            if (onCompletedStepsChange) {
                onCompletedStepsChange({
                    ...completedSteps,
                    [stepId]: {
                        ...completedSteps[stepId],
                        [substepId]: completed,
                    },
                });
            } else {
                setInternalCompletedSteps((prev: CompletedSteps) => ({
                    ...prev,
                    [stepId]: {
                        ...prev[stepId],
                        [substepId]: completed,
                    },
                }));
            }
        };

        const getOverallProgress = () => {
            const totalSteps = Object.values(completedSteps).reduce(
                (acc, step) => acc + Object.values(step).length,
                0,
            );
            const completedStepsCount = Object.values(completedSteps).reduce(
                (acc, step) => acc + Object.values(step).filter(Boolean).length,
                0,
            );
            return {
                completed: completedStepsCount,
                total: totalSteps,
                percentage: (completedStepsCount / totalSteps) * 100,
            };
        };

        // Expose methods to parent component via ref
        useImperativeHandle(ref, () => ({
            completeAllSteps,
            resetAllSteps,
            setStepCompletion,
            getOverallProgress,
            getCompletedSteps: () => completedSteps,
        }));

        // Style objects
        const containerStyle = {
            maxWidth: "42rem",
            margin: "0 auto",
            padding: "1.5rem",
            backgroundColor: "white",
        };

        const titleStyle = {
            fontSize: "1.5rem",
            fontWeight: "bold",
            color: "#1f2937",
            marginBottom: "2rem",
        };

        const stepContainerStyle = {
            border: "1px solid #e5e7eb",
            borderRadius: "0.5rem",
            overflow: "hidden",
            marginBottom: "1.5rem",
        };

        const stepHeaderStyle = (completed: boolean) => ({
            padding: "1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: completed ? "#f0fdf4" : "#f9fafb",
        });

        const stepHeaderContentStyle = {
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
        };

        const stepNumberStyle = (completed: boolean) => ({
            width: "2rem",
            height: "2rem",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: completed ? "#10b981" : "#d1d5db",
            color: completed ? "white" : "#4b5563",
        });

        const stepTitleStyle = (completed: boolean) => ({
            fontSize: "1.125rem",
            fontWeight: "600",
            color: completed ? "#047857" : "#1f2937",
        });

        const stepProgressStyle = {
            fontSize: "0.875rem",
            color: "#4b5563",
        };

        const substepsContainerStyle = {
            backgroundColor: "white",
        };

        const substepStyle = (completed: boolean, readOnly: boolean) => ({
            padding: "1rem",
            borderTop: "1px solid #f3f4f6",
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            cursor: readOnly ? "default" : "pointer",
            backgroundColor: completed ? "#f0fdf4" : "transparent",
            transition: "background-color 0.2s",
        });

        const substepIconContainerStyle = {
            marginLeft: "1rem",
        };

        const substepIconStyle = (completed: boolean) => ({
            width: "1.5rem",
            height: "1.5rem",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "2px solid",
            borderColor: completed ? "#10b981" : "#d1d5db",
            backgroundColor: completed ? "#10b981" : "transparent",
            color: completed ? "white" : "#9ca3af",
        });

        const substepTitleStyle = (completed: boolean) => ({
            flex: 1,
            color: completed ? "#047857" : "#374151",
            fontWeight: completed ? "500" : "normal",
        });

        const completedTextStyle = {
            color: "#10b981",
            fontSize: "0.875rem",
            fontWeight: "500",
        };

        const overallProgressContainerStyle = {
            marginTop: "2rem",
            padding: "1rem",
            backgroundColor: "#f9fafb",
            borderRadius: "0.5rem",
        };

        const overallProgressHeaderStyle = {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "0.5rem",
        };

        const overallProgressLabelStyle = {
            fontSize: "0.875rem",
            fontWeight: "500",
            color: "#374151",
        };

        const overallProgressTextStyle = {
            fontSize: "0.875rem",
            color: "#4b5563",
        };

        const progressBarContainerStyle = {
            width: "100%",
            backgroundColor: "#e5e7eb",
            borderRadius: "9999px",
            height: "0.5rem",
        };

        const progressBarFillStyle = (percentage: number) => ({
            backgroundColor: "#10b981",
            height: "0.5rem",
            borderRadius: "9999px",
            transition: "width 0.3s",
            width: `${percentage}%`,
        });

        return (
            <div
                style={{
                    ...containerStyle,
                    ...(className ? { className } : {}),
                }}
            >
                <h2 style={titleStyle}>Progress Tracker</h2>

                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1.5rem",
                    }}
                >
                    {progressData.map((step, stepIndex) => {
                        const stepCompleted = isStepCompleted(step.id);
                        const progress = getStepProgress(step.id);

                        return (
                            <div key={step.id} style={stepContainerStyle}>
                                {/* Main Step Header */}
                                <div style={stepHeaderStyle(stepCompleted)}>
                                    <div style={stepHeaderContentStyle}>
                                        <div
                                            style={stepNumberStyle(
                                                stepCompleted,
                                            )}
                                        >
                                            {stepCompleted ? (
                                                <CheckOutlined
                                                    style={{
                                                        width: "1.25rem",
                                                        height: "1.25rem",
                                                    }}
                                                />
                                            ) : (
                                                <span
                                                    style={{
                                                        fontSize: "0.875rem",
                                                        fontWeight: "600",
                                                    }}
                                                >
                                                    {stepIndex + 1}
                                                </span>
                                            )}
                                        </div>
                                        <h3
                                            style={stepTitleStyle(
                                                stepCompleted,
                                            )}
                                        >
                                            {step.title}
                                        </h3>
                                    </div>
                                    <div style={stepProgressStyle}>
                                        {progress.completed}/{progress.total}{" "}
                                        completed
                                    </div>
                                </div>

                                {/* Substeps */}
                                <div style={substepsContainerStyle}>
                                    {step.substeps.map(
                                        (substep, substepIndex) => {
                                            const isCompleted =
                                                completedSteps[step.id][
                                                    substep.id as keyof (typeof completedSteps)[typeof step.id]
                                                ];

                                            return (
                                                <div
                                                    key={substep.id}
                                                    style={substepStyle(
                                                        isCompleted,
                                                        readOnly,
                                                    )}
                                                    onClick={() =>
                                                        toggleStepCompletion(
                                                            step.id,
                                                            substep.id,
                                                        )
                                                    }
                                                    onMouseEnter={(e) => {
                                                        if (
                                                            !readOnly &&
                                                            !isCompleted
                                                        ) {
                                                            e.currentTarget.style.backgroundColor =
                                                                "#f9fafb";
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (
                                                            !readOnly &&
                                                            !isCompleted
                                                        ) {
                                                            e.currentTarget.style.backgroundColor =
                                                                "transparent";
                                                        }
                                                    }}
                                                >
                                                    <div
                                                        style={
                                                            substepIconContainerStyle
                                                        }
                                                    >
                                                        <div
                                                            style={substepIconStyle(
                                                                isCompleted,
                                                            )}
                                                        >
                                                            {isCompleted ? (
                                                                <CheckOutlined
                                                                    style={{
                                                                        width: "1rem",
                                                                        height: "1rem",
                                                                    }}
                                                                />
                                                            ) : (
                                                                <span
                                                                    style={{
                                                                        fontSize:
                                                                            "0.75rem",
                                                                    }}
                                                                >
                                                                    {stepIndex +
                                                                        1}
                                                                    .
                                                                    {substepIndex +
                                                                        1}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <span
                                                        style={substepTitleStyle(
                                                            isCompleted,
                                                        )}
                                                    >
                                                        {substep.title}
                                                    </span>
                                                    {isCompleted && (
                                                        <div
                                                            style={
                                                                completedTextStyle
                                                            }
                                                        >
                                                            Completed
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        },
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Overall Progress */}
                <div style={overallProgressContainerStyle}>
                    <div style={overallProgressHeaderStyle}>
                        <span style={overallProgressLabelStyle}>
                            Overall Progress
                        </span>
                        <span style={overallProgressTextStyle}>
                            {Object.values(completedSteps).reduce(
                                (acc, step) =>
                                    acc +
                                    Object.values(step).filter(Boolean).length,
                                0,
                            )}{" "}
                            /{" "}
                            {Object.values(completedSteps).reduce(
                                (acc, step) => acc + Object.values(step).length,
                                0,
                            )}{" "}
                            steps
                        </span>
                    </div>
                    <div style={progressBarContainerStyle}>
                        <div
                            style={progressBarFillStyle(
                                (Object.values(completedSteps).reduce(
                                    (acc, step) =>
                                        acc +
                                        Object.values(step).filter(Boolean)
                                            .length,
                                    0,
                                ) /
                                    Object.values(completedSteps).reduce(
                                        (acc, step) =>
                                            acc + Object.values(step).length,
                                        0,
                                    )) *
                                    100,
                            )}
                        />
                    </div>
                </div>
            </div>
        );
    },
);

AutomationProgressComponent.displayName = "AutomationProgressComponent";

export default AutomationProgressComponent;
